/* Taana — predictive search (progressive enhancement of the overlay form). */
(() => {
  const RECENT_KEY = 'taana:recent-searches';
  const MAX_RECENT = 6;

  const readRecent = () => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
  };
  const writeRecent = (list) => {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT))); } catch (e) { /* storage unavailable */ }
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.idle = this.querySelector('[data-search-idle]');
      this.reset = this.querySelector('[data-search-reset]');
      this.enabled = this.dataset.enabled === 'true';
      this.cache = new Map();
      if (!this.form || !this.input) return;

      this.onInput = Theme.utils.debounce(() => this.search(), 260);
      this.onSubmit = () => this.remember(this.input.value);
      this.onKeydown = (e) => this.navigate(e);
      this.onReset = () => setTimeout(() => { this.clear(); this.input.focus(); }, 0);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      this.form.addEventListener('submit', this.onSubmit);
      this.form.addEventListener('reset', this.onReset);
      if (this.reset) this.reset.hidden = !this.term;
      this.renderRecent();
    }

    disconnectedCallback() {
      if (!this.input) return;
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('keydown', this.onKeydown);
      this.form.removeEventListener('submit', this.onSubmit);
      this.form.removeEventListener('reset', this.onReset);
      this.abort && this.abort.abort();
    }

    get term() {
      return this.input.value.trim();
    }

    async search() {
      const term = this.term;
      if (this.reset) this.reset.hidden = !term;
      if (!term) return this.clear();
      if (!this.enabled) return;

      if (this.cache.has(term)) return this.render(this.cache.get(term));

      this.abort && this.abort.abort();
      this.abort = new AbortController();
      this.setAttribute('loading', '');
      try {
        const params = new URLSearchParams({
          q: term,
          section_id: 'predictive-search',
          'resources[type]': 'product,collection,page,article,query',
          'resources[limit]': '6',
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
        });
        const res = await fetch(`${Theme.routes.predictiveSearch}?${params}`, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('#shopify-section-predictive-search');
        const markup = content ? content.innerHTML : '';
        this.cache.set(term, markup);
        if (term === this.term) this.render(markup);
      } catch (e) {
        if (e.name !== 'AbortError') this.clear();
      } finally {
        this.removeAttribute('loading');
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.results.hidden = false;
      if (this.idle) this.idle.hidden = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.activeIndex = -1;
    }

    clear() {
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.idle) this.idle.hidden = false;
      if (this.reset) this.reset.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.renderRecent();
    }

    remember(term) {
      term = (term || '').trim();
      if (!term) return;
      const list = readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      writeRecent(list);
    }

    renderRecent() {
      const wrap = this.querySelector('[data-recent-searches]');
      const list = this.querySelector('[data-recent-list]');
      if (!wrap || !list) return;
      const items = readRecent();
      list.replaceChildren(
        ...items.map((term) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'search-chip';
          a.href = `${Theme.routes.search}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
          a.textContent = term;
          li.appendChild(a);
          return li;
        })
      );
      wrap.hidden = items.length === 0;
    }

    /* Arrow-key navigation across result links (combobox pattern). */
    navigate(e) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const options = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      if (e.key === 'Enter') {
        const active = options[this.activeIndex];
        const link = active && active.querySelector('a');
        if (link) { e.preventDefault(); this.remember(this.term); window.location.href = link.href; }
        return;
      }
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + dir + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.activeIndex)));
      const active = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
